import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FadeUp } from "@/components/Reveal";
import { EVENT, GUEST_LIST } from "@/data/content";
import { googleCalendarUrl, icsDataUri } from "@/lib/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_INTRO = {
  list: "Find your name on the guest list to begin. Seats are held in the order requests are received.",
  form: "A few final details, and your seat is secured.",
  done: "Your reservation request has been received.",
};

const initialForm = {
  guest_name: "",
  email: "",
  attendance: "attending",
  dietary_restrictions: "",
  notes: "",
};

export const RSVP = () => {
  const [step, setStep] = useState("list"); // 'list' | 'form' | 'done'
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handlePickGuest = (name) => {
    setForm({ ...initialForm, guest_name: name });
    setStep("form");
    // Scroll the form into view after state flip
    setTimeout(() => {
      const el = document.getElementById("rsvp-form-card");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const handleBack = () => {
    setStep("list");
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Please add your email so we can confirm.");
      return;
    }
    setSubmitting(true);
    try {
      // Backend expects 'name' field; we send guest_name as name
      await axios.post(`${API}/rsvp`, {
        name: form.guest_name,
        email: form.email,
        attendance: form.attendance,
        dietary_restrictions: form.dietary_restrictions,
        notes: form.notes,
      });
      setStep("done");
      toast.success("Reservation received. We will be in touch shortly.");
      // Snap the confirmation panel into view so guests see it immediately
      setTimeout(() => {
        const el = document.getElementById("rsvp-confirmation-card");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="rsvp"
      data-testid="rsvp-section"
      className="section-pad relative bg-[#0a0a0a]"
    >
      <div className="max-w-[1200px] mx-auto">
        {/* Intro */}
        <FadeUp className="text-center max-w-[720px] mx-auto mb-14 md:mb-20">
          <div className="eyebrow-line justify-center inline-flex">
            Reserve Your Seat
          </div>
          <h2
            className="font-serif-display text-[#FDFBF7] mt-6 leading-[1.05]"
            style={{ fontSize: "clamp(36px, 5vw, 56px)" }}
          >
            By invitation.{" "}
            <span className="italic text-[#D4AF37]">By reservation.</span>
          </h2>
          <p className="mt-6 text-[#FDFBF7]/85 leading-[1.75] font-light max-w-[58ch] mx-auto">
            {SECTION_INTRO[step]}
          </p>
        </FadeUp>

        {/* Event details strip */}
        {step !== "done" && (
          <FadeUp delay={0.1} className="mb-14 md:mb-16">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/20 max-w-[1000px] mx-auto">
              {[
                ["Date", EVENT.dateLong],
                ["Time", EVENT.time],
                ["Address", EVENT.address],
                ["City", EVENT.city],
                ["Dress", "Cocktail elegant"],
              ].map(([k, v]) => (
                <div key={k} className="bg-[#0f0f0f] px-5 py-5 md:py-6">
                  <div className="overline text-[10px]">{k}</div>
                  <div className="text-[#FDFBF7] text-sm md:text-[15px] mt-2 leading-snug font-light">
                    {v}
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
        )}

        {/* STEP 1: GUEST LIST */}
        {step === "list" && (
          <FadeUp delay={0.15}>
            <div className="max-w-[900px] mx-auto" data-testid="guest-list">
              <div className="text-center mb-10">
                <div className="overline">The Guest List</div>
                <div
                  className="mt-5 inline-flex items-center gap-3 px-7 py-4 border-2 border-[#D4AF37] bg-[#D4AF37]/10 rounded-full"
                  style={{
                    boxShadow:
                      "0 0 28px rgba(212,175,55,0.25), inset 0 0 12px rgba(212,175,55,0.08)",
                    animation: "rsvpPulse 2.6s ease-in-out infinite",
                  }}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full bg-[#D4AF37] animate-pulse"
                    aria-hidden="true"
                  />
                  <span className="font-serif-display text-[#FDFBF7] text-xl md:text-2xl italic">
                    Tap your name to RSVP
                  </span>
                </div>
                <style>{`@keyframes rsvpPulse { 0%, 100% { box-shadow: 0 0 28px rgba(212,175,55,0.25), inset 0 0 12px rgba(212,175,55,0.08); } 50% { box-shadow: 0 0 44px rgba(212,175,55,0.45), inset 0 0 16px rgba(212,175,55,0.12); } }`}</style>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                {GUEST_LIST.map((name, i) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handlePickGuest(name)}
                    data-testid={`guest-card-${i}`}
                    className="group relative text-left px-6 py-7 border border-[#D4AF37]/25 bg-[#0f0f0f] hover:bg-[#141414] hover:border-[#D4AF37] transition-all duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] group-hover:text-[#D4AF37] transition-colors">
                          Guest {String(i + 1).padStart(2, "0")}
                        </div>
                        <div className="font-serif-display text-[#FDFBF7] text-2xl md:text-[26px] mt-2 leading-tight">
                          {name}
                        </div>
                      </div>
                      <span
                        aria-hidden="true"
                        className="text-[#D4AF37] text-xl translate-x-0 group-hover:translate-x-1 transition-transform"
                      >
                        →
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-center text-[#7a7a7a] text-xs tracking-[0.3em] uppercase mt-12">
                Don't see your name? Reply to your invitation message.
              </p>
            </div>
          </FadeUp>
        )}

        {/* STEP 2: FORM */}
        {step === "form" && (
          <FadeUp>
            <form
              id="rsvp-form-card"
              onSubmit={handleSubmit}
              data-testid="rsvp-form"
              className="border border-[#D4AF37]/30 p-7 md:p-12 bg-[#0f0f0f] max-w-[820px] mx-auto"
              noValidate
            >
              <button
                type="button"
                onClick={handleBack}
                data-testid="rsvp-back-btn"
                className="text-[#A3A3A3] hover:text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-8 inline-flex items-center gap-2 transition-colors"
              >
                <span aria-hidden="true">←</span> Back to guest list
              </button>

              <div className="border-l-2 border-[#D4AF37] pl-5 mb-9">
                <div className="overline">Reserving as</div>
                <div
                  className="font-serif-display text-[#FDFBF7] mt-2 leading-none"
                  style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
                  data-testid="rsvp-selected-guest"
                >
                  {form.guest_name}
                </div>
              </div>

              <div>
                <label className="rpm-label" htmlFor="rsvp-email">
                  Your Email
                </label>
                <input
                  id="rsvp-email"
                  data-testid="rsvp-input-email"
                  className="rpm-input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={update("email")}
                  required
                />
              </div>

              <div className="mt-7">
                <label className="rpm-label">Attendance</label>
                <div
                  role="radiogroup"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  data-testid="rsvp-attendance"
                >
                  {[
                    { v: "attending", l: "Joyfully Attending" },
                    { v: "not_attending", l: "Regretfully Declining" },
                  ].map((opt) => {
                    const active = form.attendance === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        data-testid={`rsvp-attendance-${opt.v}`}
                        onClick={() =>
                          setForm((f) => ({ ...f, attendance: opt.v }))
                        }
                        className="text-left px-5 py-5 border transition-colors duration-200"
                        style={{
                          borderColor: active
                            ? "#D4AF37"
                            : "rgba(212,175,55,0.22)",
                          background: active
                            ? "rgba(212,175,55,0.06)"
                            : "transparent",
                          color: active ? "#FDFBF7" : "#A3A3A3",
                        }}
                      >
                        <span
                          className="block text-[10px] tracking-[0.4em] uppercase"
                          style={{ color: active ? "#D4AF37" : "#7a7a7a" }}
                        >
                          {active ? "Selected" : "Choose"}
                        </span>
                        <span className="block mt-1 font-serif-display text-xl">
                          {opt.l}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-7">
                <label className="rpm-label" htmlFor="rsvp-dietary">
                  Dietary Restrictions
                </label>
                <input
                  id="rsvp-dietary"
                  data-testid="rsvp-input-dietary"
                  className="rpm-input"
                  type="text"
                  placeholder="Allergies, restrictions, preferences"
                  value={form.dietary_restrictions}
                  onChange={update("dietary_restrictions")}
                />
              </div>

              <div className="mt-7">
                <label className="rpm-label" htmlFor="rsvp-notes">
                  Special Notes
                </label>
                <textarea
                  id="rsvp-notes"
                  data-testid="rsvp-input-notes"
                  className="rpm-textarea"
                  placeholder="Celebrating an occasion? Seating preferences? Let us know."
                  value={form.notes}
                  onChange={update("notes")}
                />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <p className="text-[#7a7a7a] text-xs tracking-[0.2em] uppercase">
                  Larry will be notified
                </p>
                <button
                  type="submit"
                  className="btn-gold"
                  disabled={submitting}
                  data-testid="rsvp-submit-btn"
                >
                  {submitting ? "Sending…" : "Submit Reservation"}
                  {!submitting && <span aria-hidden="true">→</span>}
                </button>
              </div>
            </form>
          </FadeUp>
        )}

        {/* STEP 3: CONFIRMATION */}
        {step === "done" && (
          <FadeUp>
            <div
              id="rsvp-confirmation-card"
              data-testid="rsvp-confirmation"
              className="border border-[#D4AF37]/30 p-10 md:p-16 text-center bg-[#0f0f0f] max-w-[760px] mx-auto scroll-mt-6"
            >
              <div className="eyebrow-line justify-center inline-flex mx-auto">
                Confirmation
              </div>
              <h3
                className="font-serif-display text-[#FDFBF7] mt-6 leading-[1.1]"
                style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
              >
                The needle is set.
              </h3>
              <p className="mt-8 text-[#FDFBF7]/85 leading-[1.75] max-w-[48ch] mx-auto">
                Thank you,{" "}
                <span className="text-[#D4AF37] font-serif-display text-xl">
                  {form.guest_name.split(" ")[0]}
                </span>
                . Your reservation request has been received. Larry will follow
                up by email with any final notes before the evening.
              </p>
              <p className="mt-6 text-[#A3A3A3] text-sm">
                {EVENT.dateLong} · {EVENT.time}
                <br />
                {EVENT.address}, {EVENT.city}
              </p>

              {/* Add to Calendar */}
              {form.attendance === "attending" && (
                <div className="mt-10">
                  <div className="overline mb-4">Add to Calendar</div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href={googleCalendarUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="cal-google-btn"
                      className="btn-ghost"
                    >
                      Google Calendar
                    </a>
                    <a
                      href={icsDataUri()}
                      download="33-RPM-Dining.ics"
                      data-testid="cal-apple-btn"
                      className="btn-ghost"
                    >
                      Apple / Outlook (.ics)
                    </a>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleBack}
                className="btn-ghost mt-10"
                data-testid="rsvp-back-to-list"
              >
                Back to guest list
              </button>
            </div>
          </FadeUp>
        )}
      </div>
    </section>
  );
};
